import React, { forwardRef } from "react";

import { cn } from "src/shadcn/lib/utils";
import { CheckIcon } from "lucide-react";

export interface StepperProps {
  /** Step definitions. */
  steps: Array<{ id: string | number; label: string }>;
  /** Current active step (0-based). */
  activeIndex?: number;
  /** Callback when a step indicator is clicked. */
  onStepClick?: (index: number) => void;
  /** Additional class name for the outermost wrapper. */
  className?: string;
  /** Layout direction. */
  orientation?: "horizontal" | "vertical";
}

const Stepper = forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      steps,
      activeIndex = 0,
      onStepClick,
      className,
      orientation = "horizontal",
    },
    ref
  ) => {
    const isHorizontal = orientation === "horizontal";

    return (
      <div
        ref={ref}
        role="list"
        aria-label="Progress"
        className={cn(
          "flex gap-2",
          isHorizontal ? "flex-row items-center" : "flex-col items-start",
          className
        )}
      >
        {steps.map((step, index) => {
          const isCompleted = index < activeIndex;
          const isActive = index === activeIndex;
          const isClickable = (isCompleted || isActive) && !!onStepClick;
          const isLast = index === steps.length - 1;

          const indicator = (
            <span
              className={cn(
                "relative flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors",
                isCompleted && "bg-primary text-primary-foreground",
                isActive &&
                  "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background",
                !isCompleted &&
                  !isActive &&
                  "bg-muted text-foreground/60 ring-1 ring-border"
              )}
            >
              {isCompleted ? (
                <CheckIcon className="size-3.5" aria-hidden="true" />
              ) : (
                index + 1
              )}
            </span>
          );

          const stepContent = (
            <div
              className={cn(
                "flex min-w-0",
                isHorizontal
                  ? "flex-row items-center gap-2"
                  : "flex-row items-center gap-3"
              )}
            >
              {isClickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick(index)}
                  aria-current={isActive ? "step" : undefined}
                  className="cursor-pointer rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {indicator}
                </button>
              ) : (
                <span aria-current={isActive ? "step" : undefined}>
                  {indicator}
                </span>
              )}
              <span
                className={cn(
                  "hidden text-sm truncate max-w-full sm:inline",
                  isActive && "font-medium text-foreground",
                  isCompleted && "text-foreground",
                  !isCompleted && !isActive && "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </div>
          );

          const connector = !isLast && (
            <div
              aria-hidden="true"
              className={cn(
                "transition-colors",
                isHorizontal
                  ? "mx-2 min-w-5 flex-1 border-t-2 sm:mx-3 sm:min-w-8"
                  : "ms-[13px] min-h-6 self-stretch border-s-2",
                isCompleted ? "border-primary" : "border-dashed border-border"
              )}
            />
          );

          return (
            <React.Fragment key={step.id}>
              <div
                role="listitem"
                className={cn(
                  "flex min-w-0",
                  isHorizontal ? "shrink-0" : "flex-col"
                )}
              >
                {stepContent}
              </div>
              {connector}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
);

Stepper.displayName = "Stepper";

export { Stepper };
